'use client';

import { c as _c } from "react/compiler-runtime";
import { updateCouponOutcome } from '@svs/nova-poolbetting/core/action/coupon';
import { useDraw } from '@svs/nova-poolbetting/core/frameworks/nextjs/draw';
import { getOutcomeCount } from '@svs/nova-poolbetting/core/lib/client';
import { useCouponId, useCouponOutcomes, useCouponSettings, useCouponType } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { useEngine } from '@svs/nova-poolbetting/core/nextjs/engine';
import type { CouponIndex, Outcome } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawData, DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { EventNumber, EventType, SportType } from '@svs/nova-poolbetting/schema/sport';
import { CouponEventOutcomes, CouponEventOutcomesSrow, CouponOutcome, CouponOutcomeSrow } from '@svs/nova-poolbetting/ui/Coupon';
import { breakpoints, useMediaQueryAbove, useMediaQueryBelow, useMediaQueryBetween } from '@svs/ui/react/media-query';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { getButtonSize } from './utils/getButtonSize';
type CouponOutcomesContainerProps = {
  eventNumber: EventNumber;
  sportType: SportType;
  eventType: EventType;
  couponIndex: CouponIndex;
  className?: string;
  drawNumber: DrawNumber;
};
type TapIntent = {
  index: Outcome;
  couponIndex?: CouponIndex;
};
const getOutcomeDistribution = (drawData: DrawData | undefined, eventNumber: EventNumber): (string | null | undefined)[] | undefined => {
  if (!drawData) {
    return undefined;
  }
  const drawEvent = drawData.draw.events.find(event => event.eventNumber === eventNumber);
  if (!drawEvent || drawEvent.status !== 'COMPLETE') {
    return undefined;
  }
  const eventTypeStatisticsId = drawData.bettingEvent[drawEvent.bettingEventId]?.eventTypeStatisticsId;
  if (!eventTypeStatisticsId) {
    return undefined;
  }
  const eventStatistics = drawData.eventTypeStatistics[eventTypeStatisticsId];
  if (!eventStatistics) {
    return undefined;
  }
  return eventStatistics.distribution.current.value;
};
const getUpdatedOutcomes = (currentOutcomes: Outcome[], outcomeIndex: Outcome): Outcome[] => {
  const nextOutcomes = currentOutcomes.slice();
  const index = nextOutcomes.indexOf(outcomeIndex);
  if (index === -1) {
    nextOutcomes.push(outcomeIndex);
  } else {
    nextOutcomes.splice(index, 1);
  }
  return nextOutcomes;
};

/**
 * Component responsible for rendering the outcomes for a specific event. It uses product, eventType and sportType to determine the amount of outcomes.
 * Also this component is responsible for toggling the outcome and and fetching the selected outcomes.
 *
 * @param root0 props
 * @param root0.eventNumber Number for event
 * @param root0.sportType Sport for event
 * @param root0.eventType Type of event (event_1x2, goal_count etc)
 * @param root0.couponIndex Index of the coupon, used for multi coupon
 * @param root0.className Optional classname for the component
 * @param root0.drawNumber Optional draw number for the event
 * @returns component that renders outcomes for a specific event
 */
export default function CouponOutcomesContainer(t0) {
  const $ = _c(48);
  const {
    eventNumber,
    sportType,
    eventType,
    couponIndex,
    className,
    drawNumber
  } = t0;
  const [couponId] = useCouponId();
  let t1;
  if ($[0] !== couponId || $[1] !== couponIndex) {
    t1 = {
      couponId,
      couponIndex
    };
    $[0] = couponId;
    $[1] = couponIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const couponType = useCouponType(t1);
  const isSingleRows = couponType === "singlerows";
  const definition = useEngine();
  let t2;
  if ($[3] !== definition.model || $[4] !== eventType || $[5] !== sportType) {
    t2 = getOutcomeCount(sportType, eventType, definition.model);
    $[3] = definition.model;
    $[4] = eventType;
    $[5] = sportType;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const outcomeCount = t2;
  const settings = useCouponSettings(definition.moduleEngine, "PLAY");
  let t3;
  if ($[7] !== definition.moduleEngine || $[8] !== drawNumber) {
    t3 = {
      drawNumber,
      product: definition.moduleEngine
    };
    $[7] = definition.moduleEngine;
    $[8] = drawNumber;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const draw = useDraw(t3);
  let localDistribution = undefined;
  if (settings.data.outcomeLabel === "LocalDistribution") {
    let t4;
    if ($[10] !== draw.data || $[11] !== eventNumber) {
      t4 = getOutcomeDistribution(draw.data, eventNumber);
      $[10] = draw.data;
      $[11] = eventNumber;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    localDistribution = t4;
  }
  const {
    regBetDisabled
  } = draw.data.draw;
  let t4;
  if ($[13] !== couponId || $[14] !== couponIndex || $[15] !== eventNumber) {
    t4 = {
      couponId,
      couponIndex,
      eventNumber,
      groupIndex: 0
    };
    $[13] = couponId;
    $[14] = couponIndex;
    $[15] = eventNumber;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const outcomes = useCouponOutcomes(t4);
  const isMobile = useMediaQueryBelow(breakpoints.sm);
  const isTablet = useMediaQueryBetween(breakpoints.sm, breakpoints.md);
  const isDesktop = useMediaQueryAbove(breakpoints.md);
  let t5;
  if ($[17] !== isDesktop || $[18] !== isMobile || $[19] !== isTablet) {
    t5 = getButtonSize({
      isDesktop,
      isTablet,
      isMobile
    });
    $[17] = isDesktop;
    $[18] = isMobile;
    $[19] = isTablet;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const buttonSize = t5;
  const outcomesRef = useRef(outcomes);
  const tapIntentRef = useRef(undefined);
  let t6;
  let t7;
  if ($[21] !== outcomes) {
    t6 = () => {
      outcomesRef.current = outcomes;
    };
    t7 = [outcomes];
    $[21] = outcomes;
    $[22] = t6;
    $[23] = t7;
  } else {
    t6 = $[22];
    t7 = $[23];
  }
  useEffect(t6, t7);
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const tapIntent = tapIntentRef.current;
      tapIntentRef.current = undefined;
      return tapIntent;
    };
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const consumeTapIntent = t8;
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      tapIntentRef.current = undefined;
    };
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const clearTapIntent = t9;
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (index, tapIntentCouponIndex) => {
      tapIntentRef.current = {
        index,
        couponIndex: tapIntentCouponIndex
      };
    };
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const setTapIntent = t10;
  let t11;
  if ($[27] !== couponId || $[28] !== couponIndex || $[29] !== eventNumber || $[30] !== isSingleRows) {
    t11 = (outcomeIndex, callbackCouponIndex) => {
      const tapIntentAtRequest = consumeTapIntent();
      const shouldUseTapIntent = tapIntentAtRequest !== undefined && tapIntentAtRequest.index !== outcomeIndex;
      const resolvedOutcomeIndex = shouldUseTapIntent ? tapIntentAtRequest.index : outcomeIndex;
      const currentOutcomes = outcomesRef.current;
      const updatedOutcomes = isSingleRows ? [resolvedOutcomeIndex] : getUpdatedOutcomes(currentOutcomes, resolvedOutcomeIndex);
      const oldOutcomes = outcomesRef.current;
      outcomesRef.current = updatedOutcomes;
      const expectedOutcoms = outcomesRef.current;
      updateCouponOutcome(updatedOutcomes, {
        couponId,
        couponIndex: callbackCouponIndex ?? couponIndex,
        boardIndex: 0,
        eventNumber,
        groupIndex: 0
      }).then(result => {
        if (result === "ERROR" && outcomesRef.current === expectedOutcoms) {
          outcomesRef.current = oldOutcomes;
        }
      }).catch(() => {
        if (outcomesRef.current === expectedOutcoms) {
          outcomesRef.current = oldOutcomes;
        }
      });
    };
    $[27] = couponId;
    $[28] = couponIndex;
    $[29] = eventNumber;
    $[30] = isSingleRows;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const toggleOutcome = t11;
  const CouponEventOutcomesComponent = isSingleRows ? CouponEventOutcomesSrow : CouponEventOutcomes;
  const CouponOutcomeComponent = isSingleRows ? CouponOutcomeSrow : CouponOutcome;
  let t12;
  if ($[32] !== CouponOutcomeComponent || $[33] !== buttonSize || $[34] !== couponIndex || $[35] !== eventNumber || $[36] !== regBetDisabled) {
    t12 = props => <CouponOutcomeComponent {...props} eventNumber={eventNumber} couponIndex={couponIndex} buttonSize={buttonSize} isDisabled={regBetDisabled === "Regbet" ? true : undefined} key={props.index} onPointerDown={() => {
      setTapIntent(props.index as Outcome, couponIndex);
    }} onTouchStart={() => {
      setTapIntent(props.index as Outcome, couponIndex);
    }} onPointerCancel={clearTapIntent} onTouchCancel={clearTapIntent} />;
    $[32] = CouponOutcomeComponent;
    $[33] = buttonSize;
    $[34] = couponIndex;
    $[35] = eventNumber;
    $[36] = regBetDisabled;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== CouponEventOutcomesComponent || $[39] !== className || $[40] !== couponIndex || $[41] !== eventNumber || $[42] !== localDistribution || $[43] !== outcomeCount || $[44] !== outcomes || $[45] !== t12 || $[46] !== toggleOutcome) {
    t13 = <CouponEventOutcomesComponent className={className} selectedOutcomes={outcomes} outcomeCount={outcomeCount} onChange={toggleOutcome} eventNumber={eventNumber} couponIndex={couponIndex} localDistribution={localDistribution} renderItem={t12} />;
    $[38] = CouponEventOutcomesComponent;
    $[39] = className;
    $[40] = couponIndex;
    $[41] = eventNumber;
    $[42] = localDistribution;
    $[43] = outcomeCount;
    $[44] = outcomes;
    $[45] = t12;
    $[46] = toggleOutcome;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
}